import React, { useState } from 'react';
import { FaSearch, FaSlidersH, FaTable, FaBorderAll, FaEye, FaEyeSlash, FaEdit, FaPlus, FaTrash, FaTimes, FaCheck, FaRandom, FaArrowLeft, FaArrowRight, FaSortAmountDown } from 'react-icons/fa';
import Editable from '../../components/Editable';
import { addUnit, normalizeAppUrl, getAppBase, stripHtml, getFontStyles, resolveMediaUrl, smoothScrollTo, findAnchorElement, hexToRgba, parseAlpha } from '../../utils/styleHelpers';
import { slugify } from '../../utils/helpers';
import MediaInput from '../../components/MediaInput';
import CategoryRearrangeModal from './CategoryRearrangeModal';

export const ShopFilterBar = ({
    searchQuery,
    setSearchQuery,
    search,
    setSearch,
    sortBy = 'category',
    setSortBy,
    catalogViewMode = 'grid',
    setCatalogViewMode,
    categories = [],
    activeCategory = 'All',
    setActiveCategory,
    activeSubCategory = 'All',
    setActiveSubCategory,
    subCategoriesOfActive = [],
    hiddenCatNames = new Set(),
    isCategoryMatch,
    isSubCategoryMatch,
    hideCategories = false,
    navigate,
    styleSettings = {},
    settings = {},
    typography = {},
    isEditing = false,
    showUnpublishedInEdit = true,
    isMobile = false,
    isTablet = false,
    primaryColor = '#3b82f6',
    update,
    _getFontStyles,
    dbCategories = [],
    setDbCategories,
    handleToggleHideCategory,
    handleEditCategory,
    handleCreateCategory,
    handleDeleteCategory,
    handleShuffleCategories,
    handleReorderCategories,
    handleMoveCategory,
    handleSortCategoriesAlpha,
    catSortMode,
    pages = [],
    products = []
}) => {
    const [editingCatModal, setEditingCatModal] = useState(null);
    const [isRearrangeModalOpen, setIsRearrangeModalOpen] = useState(false);
    const currentSearch = searchQuery !== undefined ? searchQuery : (search !== undefined ? search : '');
    const handleSearchChange = setSearchQuery || setSearch || (() => {});

    const defaultCatMatch = (c1, c2) => {
        if (!c2 || c2 === 'All') return true;
        if (!c1) return false;
        if (c1 === c2) return true;
        const n1 = String(c1).toLowerCase().trim();
        const n2 = String(c2).toLowerCase().trim();
        return n1 === n2 || slugify(n1) === slugify(n2);
    };
    const catMatchFn = isCategoryMatch || defaultCatMatch;
    const subCatMatchFn = isSubCategoryMatch || defaultCatMatch;

    const scrollToProductCard = (targetAnchor) => {
        const rawId = String(targetAnchor || '').trim().replace(/^#/, '');
        if (!rawId) return false;

        const targetEl = findAnchorElement(rawId) ||
                         document.getElementById(rawId) || 
                         document.getElementById(`product-${rawId}`) ||
                         document.querySelector(`[data-product-id="${rawId}"]`) ||
                         document.querySelector(`[data-product-slug="${rawId}"]`);

        if (targetEl) {
            // Use animated smoothScrollTo (RAF-based) instead of scrollIntoView which is uncontrolled
            smoothScrollTo(rawId, 600, 80);
            targetEl.classList.remove('product-anchor-target');
            requestAnimationFrame(() => {
                requestAnimationFrame(() => {
                    if (targetEl) {
                        targetEl.classList.add('product-anchor-target');
                        setTimeout(() => targetEl.classList.remove('product-anchor-target'), 2000);
                    }
                });
            });
            return true;
        }
        return false;
    };

    const handleCategoryClick = (e, catName, catObject) => {
        if (e && e.preventDefault) e.preventDefault();

        // 1. Product Card / Section Anchor Link
        if (catObject && (catObject.linkType === 'product_anchor' || (catObject.customUrl && catObject.customUrl.startsWith('#')))) {
            const anchorTarget = (catObject.customUrl || '').trim();
            const rawId = anchorTarget.replace(/^#/, '');
            const found = scrollToProductCard(rawId) || smoothScrollTo(rawId, 600, 80);
            if (!found && rawId) {
                // Target not in DOM yet — update the hash and let ShopPage's hash-scroll useEffect handle it
                if (navigate) {
                    navigate(normalizeAppUrl(`/shop#${rawId}`));
                } else {
                    try { window.history.pushState(null, '', `#${rawId}`); } catch (_) {}
                    // Retry after a tick in case element renders after category navigation
                    setTimeout(() => smoothScrollTo(rawId, 600, 80), 300);
                }
            }
            return;
        }

        // 2. Custom Page / External URL Link
        if (catObject && (catObject.linkType === 'page' || catObject.linkType === 'url') && catObject.customUrl && catObject.customUrl.trim()) {
            const dest = catObject.customUrl.trim();
            const target = catObject.target || '_self';
            if (target === '_blank') {
                window.open(dest, '_blank', 'noopener,noreferrer');
                return;
            }
            if (/^(https?:|\/\/|mailto:|tel:)/i.test(dest)) {
                window.location.href = dest;
                return;
            }
            if (navigate) {
                navigate(normalizeAppUrl(dest));
            } else {
                const _base = (getAppBase() || '').replace(/\/$/, '');
                const targetUrl = _base ? `${_base}${normalizeAppUrl(dest)}` : normalizeAppUrl(dest);
                try { window.history.pushState(null, '', targetUrl); } catch (navErr) {}
            }
            window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
            return;
        }

        // 3. Standard Category Filter navigation
        if (setActiveCategory) setActiveCategory(catName);
        if (setActiveSubCategory) setActiveSubCategory('All');
        const _relUrl = catName === 'All' ? normalizeAppUrl('/shop') : normalizeAppUrl(`/products/${slugify(catName)}`);
        if (navigate) {
            navigate(_relUrl, { preventScrollReset: true, state: { preventScrollReset: true } });
        } else {
            const _base = (getAppBase() || '').replace(/\/$/, '');
            const targetUrl = _base ? `${_base}${_relUrl}` : _relUrl;
            try { window.history.pushState({ preventScrollReset: true }, '', targetUrl); } catch (navErr) {}
        }
        
    };

    const handleSubCategoryClick = (e, subCat) => {
        if (e && e.preventDefault) e.preventDefault();

        // 1. Product Card / Section Anchor Link
        if (subCat && (subCat.linkType === 'product_anchor' || (subCat.customUrl && subCat.customUrl.startsWith('#')))) {
            const anchorTarget = (subCat.customUrl || '').trim();
            const rawId = anchorTarget.replace(/^#/, '');
            const found = scrollToProductCard(rawId) || smoothScrollTo(rawId, 600, 80);
            if (!found && rawId) {
                if (navigate) {
                    navigate(normalizeAppUrl(`/shop#${rawId}`));
                } else {
                    try { window.history.pushState(null, '', `#${rawId}`); } catch (_) {}
                    setTimeout(() => smoothScrollTo(rawId, 600, 80), 300);
                }
            }
            return;
        }

        // 2. Custom Page / External URL Link
        if (subCat && (subCat.linkType === 'page' || subCat.linkType === 'url') && subCat.customUrl && subCat.customUrl.trim()) {
            const dest = subCat.customUrl.trim();
            const target = subCat.target || '_self';
            if (target === '_blank') {
                window.open(dest, '_blank', 'noopener,noreferrer');
                return;
            }
            if (/^(https?:|\/\/|mailto:|tel:)/i.test(dest)) {
                window.location.href = dest;
                return;
            }
            if (navigate) {
                navigate(normalizeAppUrl(dest));
            } else {
                const _base = (getAppBase() || '').replace(/\/$/, '');
                const targetUrl = _base ? `${_base}${normalizeAppUrl(dest)}` : normalizeAppUrl(dest);
                try { window.history.pushState(null, '', targetUrl); } catch (navErr) {}
            }
            window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
            return;
        }

        // 3. Standard Sub-category Filter navigation
        if (setActiveSubCategory) setActiveSubCategory(subCat.name);
        const _relUrl = `${normalizeAppUrl(`/products/${slugify(activeCategory)}`)}?subcategory=${encodeURIComponent(subCat.name)}`;
        if (navigate) {
            navigate(_relUrl, { preventScrollReset: true, state: { preventScrollReset: true } });
        } else {
            const _base = (getAppBase() || '').replace(/\/$/, '');
            const targetUrl = _base ? `${_base}${_relUrl}` : _relUrl;
            try { window.history.pushState({ preventScrollReset: true }, '', targetUrl); } catch (navErr) {}
        }
    };

    const resolveFontStyles = typeof _getFontStyles === 'function' 
        ? _getFontStyles 
        : getFontStyles(typography || settings?.typography || {});

    const catDisplayMode = styleSettings.shopCardCategoryDisplay || styleSettings.shopCardCatDisplayMode || styleSettings.shopCatDisplayMode || 'text'; // 'text' | 'image' | 'both'
    const catImageWidth = styleSettings.shopCardCatImageWidth || styleSettings.shopCardCatImageSize || styleSettings.shopCatImageWidth || styleSettings.shopCatImageSize || '28px';
    const catImageHeight = styleSettings.shopCardCatImageHeight || styleSettings.shopCardCatImageSize || styleSettings.shopCatImageHeight || styleSettings.shopCatImageSize || catImageWidth;
    const catImageRadius = styleSettings.shopCardCatImageRadius !== undefined ? styleSettings.shopCardCatImageRadius : (styleSettings.shopCatImageRadius || '50%');
    const catImageFit = styleSettings.shopCardCatImageFit || styleSettings.shopCatImageFit || 'cover';
    const catImageGap = styleSettings.shopCardCatImageGap || styleSettings.shopCatImageGap || '8px';
    const catImagePosition = styleSettings.shopCardCatImagePosition || styleSettings.shopCatImagePosition || 'left'; // 'left' | 'top' | 'right' | 'bottom'
    const catImageBorder = styleSettings.shopCardCatImageBorder || styleSettings.shopCatImageBorder || 'none';
    const catImageShadow = styleSettings.shopCardCatImageShadow || styleSettings.shopCatImageShadow || 'none';
    const catImageBg = styleSettings.shopCardCatImageBg || styleSettings.shopCatImageBg || 'transparent';
    const catImagePadding = styleSettings.shopCardCatImagePadding || styleSettings.shopCatImagePadding || '0px';

    const renderCatContent = (catName, catImageUrl, isSub = false) => {
        const textLabel = stripHtml(catName);
        const resolvedImg = catImageUrl ? resolveMediaUrl(catImageUrl) : null;
        
        const rawW = addUnit(catImageWidth);
        const rawH = addUnit(catImageHeight);
        const currentWidth = isSub ? (styleSettings.shopCatSubImageWidth ? addUnit(styleSettings.shopCatSubImageWidth) : `calc(${rawW} * 0.85)`) : rawW;
        const currentHeight = isSub ? (styleSettings.shopCatSubImageHeight ? addUnit(styleSettings.shopCatSubImageHeight) : `calc(${rawH} * 0.85)`) : rawH;

        const getFlexDir = () => {
            if (catImagePosition === 'top') return 'column';
            if (catImagePosition === 'bottom') return 'column-reverse';
            if (catImagePosition === 'right') return 'row-reverse';
            return 'row';
        };

        const imgStyle = {
            width: currentWidth,
            height: currentHeight,
            maxWidth: '100%',
            borderRadius: addUnit(catImageRadius),
            objectFit: catImageFit,
            border: catImageBorder,
            boxShadow: catImageShadow,
            background: catImageBg,
            padding: addUnit(catImagePadding),
            boxSizing: 'border-box',
            display: 'block',
            pointerEvents: 'none',
            flexShrink: 0
        };

        if (catDisplayMode === 'image') {
            if (resolvedImg) {
                return (
                    <img 
                        src={resolvedImg} 
                        alt={textLabel} 
                        title={textLabel} 
                        style={imgStyle} 
                    />
                );
            }
            return <span>{textLabel}</span>;
        }

        if (catDisplayMode === 'both') {
            if (resolvedImg) {
                return (
                    <span style={{ 
                        display: 'inline-flex', 
                        alignItems: 'center', 
                        justifyContent: 'center', 
                        gap: addUnit(catImageGap),
                        flexDirection: getFlexDir(),
                        width: (catImagePosition === 'top' || catImagePosition === 'bottom') ? '100%' : 'auto'
                    }}>
                        <img 
                            src={resolvedImg} 
                            alt="" 
                            style={imgStyle} 
                        />
                        <span style={{ textAlign: 'center', wordBreak: 'break-word' }}>{textLabel}</span>
                    </span>
                );
            }
            return <span>{textLabel}</span>;
        }

        return textLabel;
    };

    const searchPlacement = styleSettings.shopSearchPlacement || 'inside-right';

    const isSearchShown = (styleSettings.shopSearchShow !== false && styleSettings.shopSearchShow !== 'false') && (searchPlacement !== 'hidden');

    const renderSearchInput = (customStyle = {}) => {
        if (!isSearchShown) return null;
        return (
            <div className="shop-search-wrapper" style={{ 
                position: 'relative', 
                display: 'inline-flex', 
                alignItems: 'center', 
                height: '38px', 
                width: '100%', 
                minWidth: '180px', 
                boxSizing: 'border-box', 
                verticalAlign: 'middle',
                margin: 0,
                ...customStyle 
            }}>
                <FaSearch style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8', fontSize: '0.85rem', pointerEvents: 'none', zIndex: 1 }} />
                <input
                    type="text"
                    placeholder={styleSettings.shopSearchPlaceholder || "Search products..."}
                    value={currentSearch}
                    onChange={e => handleSearchChange(e.target.value)}
                    style={{
                        width: '100%',
                        height: '38px',
                        lineHeight: '38px',
                        padding: '0 12px 0 36px',
                        margin: 0,
                        border: styleSettings.shopSearchBorder || '1px solid #cbd5e1',
                        borderRadius: styleSettings.shopSearchRadius || '20px',
                        fontSize: addUnit(styleSettings.shopSearchFontSize || '0.85rem'),
                        background: styleSettings.shopSearchBg || '#ffffff',
                        color: styleSettings.shopSearchColor || '#1e293b',
                        boxShadow: styleSettings.shopSearchShadow || 'none',
                        outline: 'none',
                        boxSizing: 'border-box',
                        verticalAlign: 'middle'
                    }}
                />
            </div>
        );
    };

    const renderSortSelect = (customStyle = {}) => {
        if (styleSettings.shopSortShow === false || styleSettings.shopSortShow === 'false') return null;
        return (
            <div className="shop-sort-wrapper" style={{ 
                position: 'relative', 
                display: 'inline-flex', 
                alignItems: 'center', 
                height: '38px', 
                boxSizing: 'border-box', 
                verticalAlign: 'middle',
                margin: 0,
                ...customStyle 
            }}>
                <FaSlidersH style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8', fontSize: '0.8rem', pointerEvents: 'none', zIndex: 1 }} />
                <select
                    value={sortBy}
                    onChange={e => setSortBy && setSortBy(e.target.value)}
                    style={{
                        height: '38px',
                        lineHeight: '38px',
                        padding: '0 12px 0 30px',
                        margin: 0,
                        border: styleSettings.shopSortBorder || styleSettings.shopSearchBorder || '1px solid #cbd5e1',
                        borderRadius: styleSettings.shopSortRadius || styleSettings.shopSearchRadius || '20px',
                        fontSize: addUnit(styleSettings.shopSortFontSize || styleSettings.shopSearchFontSize || '0.85rem'),
                        background: styleSettings.shopSortBg || styleSettings.shopSearchBg || '#ffffff',
                        color: styleSettings.shopSortColor || styleSettings.shopSearchColor || '#1e293b',
                        cursor: 'pointer',
                        outline: 'none',
                        boxSizing: 'border-box',
                        verticalAlign: 'middle'
                    }}
                >
                    <option value="category">🏷️ Category (A → Z)</option>
                    <option value="name-asc">🔤 Name (A → Z)</option>
                    <option value="name-desc">🔤 Name (Z → A)</option>
                    <option value="price-asc">💵 Price: Low to High</option>
                    <option value="price-desc">💵 Price: High to Low</option>
                    <option value="newest">✨ Newest First</option>
                </select>
            </div>
        );
    };

    const renderViewModeToggle = (customStyle = {}) => {
        if (styleSettings.shopDisableTableToggle) return null;
        const quickOrderLabel = styleSettings.shopQuickOrderTableLabel || settings.shopQuickOrderTableLabel || 'Quick Order Table';
        return (
            <div className="shop-view-mode-toggle" style={{ 
                display: 'inline-flex', 
                alignItems: 'center', 
                height: '38px', 
                background: '#f1f5f9', 
                borderRadius: styleSettings.shopSortRadius || styleSettings.shopSearchRadius || '20px', 
                padding: '3px', 
                border: '1px solid #cbd5e1', 
                boxSizing: 'border-box',
                verticalAlign: 'middle',
                margin: 0,
                ...customStyle 
            }}>
                <button
                    type="button"
                    onClick={() => setCatalogViewMode && setCatalogViewMode('grid')}
                    title="Grid View"
                    style={{
                        height: '30px',
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '6px',
                        padding: '0 10px',
                        margin: 0,
                        border: 'none',
                        borderRadius: '16px',
                        background: catalogViewMode === 'grid' ? '#ffffff' : 'transparent',
                        color: catalogViewMode === 'grid' ? primaryColor : '#64748b',
                        boxShadow: catalogViewMode === 'grid' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                        cursor: 'pointer',
                        fontSize: '0.8rem',
                        fontWeight: '600',
                        whiteSpace: 'nowrap',
                        boxSizing: 'border-box',
                        verticalAlign: 'middle'
                    }}
                >
                    <FaBorderAll style={{ flexShrink: 0 }} />
                    <span>Grid</span>
                </button>
                <button
                    type="button"
                    onClick={() => setCatalogViewMode && setCatalogViewMode('table')}
                    title={quickOrderLabel}
                    style={{
                        height: '30px',
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '6px',
                        padding: '0 10px',
                        margin: 0,
                        border: 'none',
                        borderRadius: '16px',
                        background: catalogViewMode === 'table' ? '#ffffff' : 'transparent',
                        color: catalogViewMode === 'table' ? primaryColor : '#64748b',
                        boxShadow: catalogViewMode === 'table' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                        cursor: 'pointer',
                        fontSize: '0.8rem',
                        fontWeight: '600',
                        whiteSpace: 'nowrap',
                        boxSizing: 'border-box',
                        verticalAlign: 'middle'
                    }}
                >
                    <FaTable style={{ flexShrink: 0 }} />
                    {isEditing ? (
                        <Editable
                            as="span"
                            isEditing={isEditing}
                            value={quickOrderLabel}
                            onChange={e => update && update(['settings', 'shopQuickOrderTableLabel'], e.target.value)}
                            style={{ display: 'inline', fontSize: '0.8rem', fontWeight: '600' }}
                        />
                    ) : (
                        <span>{quickOrderLabel}</span>
                    )}
                </button>
            </div>
        );
    };

    return (
        <>
            <style>{`
                .shop-search-sort-row {
                    display: flex !important;
                    align-items: center !important;
                    gap: 10px !important;
                }
                .shop-search-sort-row > * {
                    vertical-align: middle !important;
                    align-self: center !important;
                }
                .shop-cat-btn-edit-wrapper {
                    transition: all 0.2s ease;
                }
                .shop-cat-btn-edit-wrapper:hover {
                    box-shadow: 0 2px 4px rgba(0,0,0,0.1);
                }
                @keyframes productAnchorGlow {
                    0% {
                        box-shadow: 0 0 0 4px #3b82f6, 0 12px 24px rgba(59,130,246,0.3) !important;
                        transform: scale(1.025) !important;
                    }
                    50% {
                        box-shadow: 0 0 0 6px #60a5fa, 0 16px 30px rgba(59,130,246,0.25) !important;
                        transform: scale(1.03) !important;
                    }
                    100% {
                        box-shadow: inherit !important;
                        transform: scale(1) !important;
                    }
                }
                .product-anchor-target {
                    animation: productAnchorGlow 1.8s ease-out !important;
                    z-index: 10 !important;
                    position: relative !important;
                }
            `}</style>

            {/* Category Edit / Create Modal */}
            {editingCatModal && (
                <div 
                    style={{
                        position: 'fixed',
                        inset: 0,
                        zIndex: 999999,
                        background: 'rgba(15, 23, 42, 0.65)',
                        backdropFilter: 'blur(4px)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: '20px'
                    }}
                    onClick={(e) => {
                        if (e.target === e.currentTarget) setEditingCatModal(null);
                    }}
                >
                    <div 
                        style={{
                            background: '#ffffff',
                            borderRadius: '16px',
                            width: '100%',
                            maxWidth: '480px',
                            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
                            overflow: 'hidden',
                            display: 'flex',
                            flexDirection: 'column',
                            border: '1px solid #e2e8f0'
                        }}
                        onClick={e => e.stopPropagation()}
                    >
                        {/* Header */}
                        <div style={{
                            padding: '16px 20px',
                            borderBottom: '1px solid #e2e8f0',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            background: '#f8fafc'
                        }}>
                            <h3 style={{ margin: 0, fontSize: '1.1rem', fontWeight: 700, color: '#0f172a', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                {editingCatModal.isNew ? '✨ New Category' : `✏️ Edit Category: ${stripHtml(editingCatModal.originalName || editingCatModal.name)}`}
                            </h3>
                            <button
                                type="button"
                                onClick={() => setEditingCatModal(null)}
                                style={{
                                    background: 'transparent',
                                    border: 'none',
                                    color: '#64748b',
                                    cursor: 'pointer',
                                    padding: '4px',
                                    borderRadius: '6px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                }}
                            >
                                <FaTimes size={16} />
                            </button>
                        </div>

                        {/* Body */}
                        <div style={{ padding: '20px', display: 'flex', flexDirection: 'column', gap: '16px', maxHeight: '70vh', overflowY: 'auto' }}>
                            <div>
                                <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: '#334155', marginBottom: '6px' }}>
                                    Category Name <span style={{ color: '#ef4444' }}>*</span>
                                </label>
                                <input
                                    type="text"
                                    value={editingCatModal.name}
                                    onChange={(e) => {
                                        const val = e.target.value;
                                        setEditingCatModal(prev => ({
                                            ...prev,
                                            name: val,
                                            slug: prev.isNew ? slugify(val) : prev.slug
                                        }));
                                    }}
                                    placeholder="e.g. Moonshine, Apparel, Spirits..."
                                    style={{
                                        width: '100%',
                                        padding: '8px 12px',
                                        border: '1px solid #cbd5e1',
                                        borderRadius: '8px',
                                        fontSize: '0.9rem',
                                        outline: 'none',
                                        boxSizing: 'border-box'
                                    }}
                                    autoFocus
                                />
                            </div>

                            <div>
                                <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: '#334155', marginBottom: '6px' }}>
                                    URL Slug (used in URL: /products/{editingCatModal.slug || 'slug'})
                                </label>
                                <input
                                    type="text"
                                    value={editingCatModal.slug || ''}
                                    onChange={(e) => {
                                        setEditingCatModal(prev => ({ ...prev, slug: slugify(e.target.value) }));
                                    }}
                                    placeholder="e.g. moonshine"
                                    style={{
                                        width: '100%',
                                        padding: '8px 12px',
                                        border: '1px solid #cbd5e1',
                                        borderRadius: '8px',
                                        fontSize: '0.9rem',
                                        outline: 'none',
                                        boxSizing: 'border-box',
                                        background: '#f8fafc'
                                    }}
                                />
                            </div>

                            <div>
                                <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: '#334155', marginBottom: '6px' }}>
                                    Description (Optional)
                                </label>
                                <textarea
                                    rows={3}
                                    value={editingCatModal.description || ''}
                                    onChange={(e) => {
                                        setEditingCatModal(prev => ({ ...prev, description: e.target.value }));
                                    }}
                                    placeholder="Describe this category..."
                                    style={{
                                        width: '100%',
                                        padding: '8px 12px',
                                        border: '1px solid #cbd5e1',
                                        borderRadius: '8px',
                                        fontSize: '0.9rem',
                                        outline: 'none',
                                        boxSizing: 'border-box',
                                        resize: 'vertical'
                                    }}
                                />
                            </div>

                            <div>
                                <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: '#334155', marginBottom: '6px' }}>
                                    Category Image (Optional)
                                </label>
                                <MediaInput
                                    value={editingCatModal.image || ''}
                                    onChange={(e) => {
                                        setEditingCatModal(prev => ({ ...prev, image: e.target.value }));
                                    }}
                                    placeholder="Paste URL or upload category image..."
                                />
                            </div>

                            <div>
                                <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: '#334155', marginBottom: '6px' }}>
                                    Parent Category (Optional)
                                </label>
                                <select
                                    value={editingCatModal.parentId || ''}
                                    onChange={(e) => {
                                        setEditingCatModal(prev => ({ ...prev, parentId: e.target.value || null }));
                                    }}
                                    style={{
                                        width: '100%',
                                        padding: '8px 12px',
                                        border: '1px solid #cbd5e1',
                                        borderRadius: '8px',
                                        fontSize: '0.9rem',
                                        outline: 'none',
                                        boxSizing: 'border-box',
                                        background: '#ffffff'
                                    }}
                                >
                                    <option value="">None (Top-Level Category)</option>
                                    {dbCategories
                                        .filter(c => !c.parentId && c.id !== editingCatModal.id && c.name !== editingCatModal.name)
                                        .map(c => (
                                            <option key={c.id || c.name} value={c.id || c.name}>
                                                {c.name}
                                            </option>
                                        ))
                                    }
                                </select>
                            </div>

                            {/* Category Action / Link Target */}
                            <div style={{ background: '#f8fafc', padding: '12px', borderRadius: '8px', border: '1px solid #e2e8f0', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                                <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 700, color: '#1e293b' }}>
                                    🔗 Category Click Action
                                </label>
                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '6px' }}>
                                    {[
                                        { id: 'category', label: '🏷️ Shop Filter', desc: 'Standard filter' },
                                        { id: 'product_anchor', label: '🎯 Product Card', desc: 'Scroll to Card ID' },
                                        { id: 'page', label: '📄 Site Page', desc: 'Internal page' },
                                        { id: 'url', label: '🌐 Custom URL', desc: 'External link' }
                                    ].map(opt => {
                                        const isSelected = (editingCatModal.linkType || 'category') === opt.id;
                                        return (
                                            <button
                                                key={opt.id}
                                                type="button"
                                                onClick={() => {
                                                    setEditingCatModal(prev => ({
                                                        ...prev,
                                                        linkType: opt.id,
                                                        customUrl: opt.id === 'product_anchor' && !prev.customUrl?.startsWith('#') ? (products && products[0] ? `#${products[0].cardAnchorId || products[0].anchorId || (products[0].slug ? `product-${products[0].slug}` : `product-${products[0].id}`)}` : '#') : prev.customUrl
                                                    }));
                                                }}
                                                style={{
                                                    padding: '8px 4px',
                                                    border: isSelected ? `2px solid ${primaryColor || '#2563eb'}` : '1px solid #cbd5e1',
                                                    background: isSelected ? '#eff6ff' : '#ffffff',
                                                    color: isSelected ? '#1d4ed8' : '#475569',
                                                    borderRadius: '6px',
                                                    fontWeight: isSelected ? 700 : 500,
                                                    fontSize: '0.74rem',
                                                    cursor: 'pointer',
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    alignItems: 'center',
                                                    gap: '2px',
                                                    textAlign: 'center'
                                                }}
                                            >
                                                <span>{opt.label}</span>
                                                <span style={{ fontSize: '0.62rem', color: isSelected ? '#3b82f6' : '#94a3b8' }}>{opt.desc}</span>
                                            </button>
                                        );
                                    })}
                                </div>

                                {editingCatModal.linkType === 'product_anchor' && (
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', background: '#ffffff', padding: '10px', borderRadius: '6px', border: '1px solid #cbd5e1' }}>
                                        <label style={{ fontSize: '0.75rem', fontWeight: 700, color: '#334155' }}>
                                            Select Target Product Card:
                                        </label>
                                        <select
                                            value={editingCatModal.customUrl || ''}
                                            onChange={(e) => setEditingCatModal(prev => ({ ...prev, customUrl: e.target.value }))}
                                            style={{
                                                width: '100%',
                                                padding: '8px 10px',
                                                border: '1px solid #cbd5e1',
                                                borderRadius: '6px',
                                                fontSize: '0.85rem',
                                                background: '#ffffff',
                                                boxSizing: 'border-box'
                                            }}
                                        >
                                            <option value="">-- Choose a product card to scroll to --</option>
                                            {(products || []).map(p => {
                                                const targetId = p.cardAnchorId || p.anchorId || p.cardId || (p.slug ? `product-${p.slug}` : `product-${p.id}`);
                                                const anchorVal = `#${targetId}`;
                                                return (
                                                    <option key={p.id} value={anchorVal}>
                                                        🛍️ {stripHtml(p.name)} (#{targetId})
                                                    </option>
                                                );
                                            })}
                                        </select>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                            <span style={{ fontSize: '0.72rem', color: '#64748b', whiteSpace: 'nowrap' }}>Or custom ID:</span>
                                            <span style={{ fontSize: '0.85rem', color: '#0284c7', fontWeight: 'bold' }}>#</span>
                                            <input
                                                type="text"
                                                value={(editingCatModal.customUrl || '').replace(/^#/, '')}
                                                onChange={(e) => {
                                                    const clean = e.target.value.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-_]/g, '');
                                                    setEditingCatModal(prev => ({ ...prev, customUrl: clean ? `#${clean}` : '' }));
                                                }}
                                                placeholder="e.g. vase-01 or featured-offer"
                                                style={{ flex: 1, padding: '6px 8px', fontSize: '0.8rem', border: '1px solid #cbd5e1', borderRadius: '4px', fontFamily: 'monospace', color: '#0369a1', fontWeight: '600' }}
                                            />
                                        </div>
                                        <p style={{ margin: 0, fontSize: '0.7rem', color: '#64748b' }}>
                                            💡 When clicked, the category tab will smoothly scroll straight down to the product card with this ID and highlight it.
                                        </p>
                                    </div>
                                )}

                                {editingCatModal.linkType === 'page' && (
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                        <label style={{ fontSize: '0.75rem', fontWeight: 600, color: '#475569' }}>
                                            Select Site Page:
                                        </label>
                                        <select
                                            value={editingCatModal.customUrl || ''}
                                            onChange={(e) => setEditingCatModal(prev => ({ ...prev, customUrl: e.target.value }))}
                                            style={{
                                                width: '100%',
                                                padding: '8px 10px',
                                                border: '1px solid #cbd5e1',
                                                borderRadius: '6px',
                                                fontSize: '0.85rem',
                                                background: '#ffffff',
                                                boxSizing: 'border-box'
                                            }}
                                        >
                                            <option value="">-- Choose a page --</option>
                                            <option value="/">🏠 Home Page (/)</option>
                                            <option value="/shop">🛍️ Shop Main (/shop)</option>
                                            <option value="/articles">📰 Articles / Blog (/articles)</option>
                                            <option value="/track-order">📦 Track Order (/track-order)</option>
                                            {(pages || []).map(p => {
                                                const pageUrl = `/pages/${p.slug || slugify(p.title || '')}`;
                                                return (
                                                    <option key={p.id || p.slug} value={pageUrl}>
                                                        📄 {p.title || p.slug} ({pageUrl})
                                                    </option>
                                                );
                                            })}
                                        </select>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                            <span style={{ fontSize: '0.72rem', color: '#64748b' }}>Or route:</span>
                                            <input
                                                type="text"
                                                value={editingCatModal.customUrl || ''}
                                                onChange={(e) => setEditingCatModal(prev => ({ ...prev, customUrl: e.target.value }))}
                                                placeholder="/pages/about"
                                                style={{ flex: 1, padding: '5px 8px', fontSize: '0.78rem', border: '1px solid #cbd5e1', borderRadius: '4px' }}
                                            />
                                        </div>
                                    </div>
                                )}

                                {editingCatModal.linkType === 'url' && (
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                        <label style={{ fontSize: '0.75rem', fontWeight: 600, color: '#475569' }}>
                                            Destination URL:
                                        </label>
                                        <input
                                            type="text"
                                            value={editingCatModal.customUrl || ''}
                                            onChange={(e) => setEditingCatModal(prev => ({ ...prev, customUrl: e.target.value }))}
                                            placeholder="https://example.com or /custom-url"
                                            style={{
                                                width: '100%',
                                                padding: '8px 10px',
                                                border: '1px solid #cbd5e1',
                                                borderRadius: '6px',
                                                fontSize: '0.85rem',
                                                boxSizing: 'border-box'
                                            }}
                                        />
                                        <label style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '0.75rem', color: '#475569', cursor: 'pointer', marginTop: '2px' }}>
                                            <input
                                                type="checkbox"
                                                checked={editingCatModal.target === '_blank'}
                                                onChange={(e) => setEditingCatModal(prev => ({ ...prev, target: e.target.checked ? '_blank' : '_self' }))}
                                            />
                                            Open link in a new browser tab
                                        </label>
                                    </div>
                                )}
                            </div>

                            <div>
                                <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: '#334155', marginBottom: '6px' }}>
                                    Store Visibility
                                </label>
                                <div style={{ display: 'flex', gap: '10px' }}>
                                    <button
                                        type="button"
                                        onClick={() => setEditingCatModal(prev => ({ ...prev, hidden: 0 }))}
                                        style={{
                                            flex: 1,
                                            padding: '8px 12px',
                                            border: !(editingCatModal.hidden === 1 || editingCatModal.hidden === true || editingCatModal.hidden === '1') ? '2px solid #16a34a' : '1px solid #cbd5e1',
                                            background: !(editingCatModal.hidden === 1 || editingCatModal.hidden === true || editingCatModal.hidden === '1') ? '#f0fdf4' : '#ffffff',
                                            color: !(editingCatModal.hidden === 1 || editingCatModal.hidden === true || editingCatModal.hidden === '1') ? '#16a34a' : '#64748b',
                                            borderRadius: '8px',
                                            fontWeight: 600,
                                            fontSize: '0.85rem',
                                            cursor: 'pointer',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            gap: '6px'
                                        }}
                                    >
                                        👁️ Visible to Public
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => setEditingCatModal(prev => ({ ...prev, hidden: 1 }))}
                                        style={{
                                            flex: 1,
                                            padding: '8px 12px',
                                            border: (editingCatModal.hidden === 1 || editingCatModal.hidden === true || editingCatModal.hidden === '1') ? '2px solid #ea580c' : '1px solid #cbd5e1',
                                            background: (editingCatModal.hidden === 1 || editingCatModal.hidden === true || editingCatModal.hidden === '1') ? '#fff7ed' : '#ffffff',
                                            color: (editingCatModal.hidden === 1 || editingCatModal.hidden === true || editingCatModal.hidden === '1') ? '#ea580c' : '#64748b',
                                            borderRadius: '8px',
                                            fontWeight: 600,
                                            fontSize: '0.85rem',
                                            cursor: 'pointer',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            gap: '6px'
                                        }}
                                    >
                                        🙈 Hidden from Store
                                    </button>
                                </div>
                            </div>
                        </div>

                        {/* Footer */}
                        <div style={{
                            padding: '16px 20px',
                            borderTop: '1px solid #e2e8f0',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            background: '#f8fafc'
                        }}>
                            {!editingCatModal.isNew && editingCatModal.name !== 'All' ? (
                                <button
                                    type="button"
                                    onClick={() => {
                                        if (window.confirm(`Are you sure you want to delete category "${editingCatModal.name}"?`)) {
                                            if (handleDeleteCategory) {
                                                handleDeleteCategory(editingCatModal.id || editingCatModal.name);
                                            }
                                            setEditingCatModal(null);
                                        }
                                    }}
                                    style={{
                                        background: '#fee2e2',
                                        color: '#b91c1c',
                                        border: '1px solid #fecaca',
                                        borderRadius: '8px',
                                        padding: '8px 14px',
                                        fontSize: '0.85rem',
                                        fontWeight: 600,
                                        cursor: 'pointer',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '6px'
                                    }}
                                >
                                    <FaTrash size={12} /> Delete
                                </button>
                            ) : <div />}

                            <div style={{ display: 'flex', gap: '10px' }}>
                                <button
                                    type="button"
                                    onClick={() => setEditingCatModal(null)}
                                    style={{
                                        background: '#ffffff',
                                        color: '#475569',
                                        border: '1px solid #cbd5e1',
                                        borderRadius: '8px',
                                        padding: '8px 16px',
                                        fontSize: '0.85rem',
                                        fontWeight: 600,
                                        cursor: 'pointer'
                                    }}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="button"
                                    onClick={() => {
                                        if (!editingCatModal.name || !editingCatModal.name.trim()) {
                                            alert('Please enter a category name');
                                            return;
                                        }
                                        const trimmedName = editingCatModal.name.trim();
                                        const fields = {
                                            name: trimmedName,
                                            slug: editingCatModal.slug ? slugify(editingCatModal.slug) : slugify(trimmedName),
                                            description: editingCatModal.description || '',
                                            image: editingCatModal.image || '',
                                            color: editingCatModal.color || '#3b82f6',
                                            hidden: (editingCatModal.hidden === 1 || editingCatModal.hidden === true || editingCatModal.hidden === '1') ? 1 : 0,
                                            parentId: editingCatModal.parentId || null,
                                            linkType: editingCatModal.linkType || 'category',
                                            customUrl: (editingCatModal.customUrl || '').trim(),
                                            target: editingCatModal.target || '_self'
                                        };

                                        if (editingCatModal.isNew) {
                                            if (handleCreateCategory) {
                                                handleCreateCategory(trimmedName, fields.parentId, fields);
                                            } else if (handleEditCategory) {
                                                handleEditCategory(trimmedName, fields);
                                            }
                                        } else {
                                            if (handleEditCategory) {
                                                handleEditCategory(editingCatModal.id || editingCatModal.originalName || editingCatModal.name, fields);
                                            }
                                        }
                                        setEditingCatModal(null);
                                    }}
                                    style={{
                                        background: primaryColor || '#2563eb',
                                        color: '#ffffff',
                                        border: 'none',
                                        borderRadius: '8px',
                                        padding: '8px 18px',
                                        fontSize: '0.85rem',
                                        fontWeight: 600,
                                        cursor: 'pointer',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '6px',
                                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                                    }}
                                >
                                    <FaCheck size={12} /> {editingCatModal.isNew ? 'Create Category' : 'Save Changes'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Search & Sort Controls (Above Panel Placement) */}
            {searchPlacement === 'above-panel' && (
                <div className="shop-search-sort-row" style={{ marginBottom: '16px', display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'center', justifyContent: styleSettings.shopSearchAlign === 'center' ? 'center' : (styleSettings.shopSearchAlign === 'flex-end' ? 'flex-end' : 'flex-start') }}>
                    {renderSearchInput({ flex: isMobile ? '1 1 100%' : '0 1 320px' })}
                    {renderSortSelect({ flex: isMobile ? '1 1 100%' : '0 0 auto' })}
                    {renderViewModeToggle({ flex: isMobile ? '1 1 100%' : '0 0 auto' })}
                </div>
            )}

            {/* Filter and Search Bar Panel */}
            {(() => {
                const searchIsInsidePanel = ['inside-top', 'inside-left', 'inside-right', 'inside-bottom'].includes(searchPlacement) ||
                    (!['above-panel', 'below-panel', 'hidden'].includes(searchPlacement));
                const panelHasContent = !hideCategories || searchIsInsidePanel;
                if (!panelHasContent) return null;

                const filterPanelOpacity = parseAlpha(
                    styleSettings.shopFilterPanelOpacity !== undefined
                        ? styleSettings.shopFilterPanelOpacity
                        : (styleSettings.shopCatBarOpacity !== undefined ? styleSettings.shopCatBarOpacity : 1),
                    1
                );
                const rawFilterPanelBg = styleSettings.shopFilterPanelBg || styleSettings.shopCatBarBg || '#ffffff';
                const resolvedFilterPanelBg = hexToRgba(rawFilterPanelBg, filterPanelOpacity);

                const catContainerOpacity = parseAlpha(styleSettings.shopCatContainerOpacity !== undefined ? styleSettings.shopCatContainerOpacity : 1, 1);
                const rawCatContainerBg = styleSettings.shopCatContainerBg;
                const resolvedCatContainerBg = (() => {
                    if (!rawCatContainerBg || rawCatContainerBg === 'transparent' || rawCatContainerBg === 'none' || catContainerOpacity === 0) {
                        return 'transparent';
                    }
                    return hexToRgba(rawCatContainerBg, catContainerOpacity);
                })();

                const catPadTop = styleSettings.shopCatContainerPaddingTop;
                const catPadBottom = styleSettings.shopCatContainerPaddingBottom;
                const catPadSides = styleSettings.shopCatContainerPaddingSides;
                const catPadLeft = styleSettings.shopCatContainerPaddingLeft ?? catPadSides;
                const catPadRight = styleSettings.shopCatContainerPaddingRight ?? catPadSides;

                const hasGranularCatPad = (catPadTop !== undefined && catPadTop !== '') ||
                                          (catPadRight !== undefined && catPadRight !== '') ||
                                          (catPadBottom !== undefined && catPadBottom !== '') ||
                                          (catPadLeft !== undefined && catPadLeft !== '');

                const rawCatPad = (styleSettings.shopCatContainerPadding !== undefined && styleSettings.shopCatContainerPadding !== '')
                    ? String(styleSettings.shopCatContainerPadding).trim()
                    : '';

                const shorthandSides = rawCatPad ? (() => {
                    const parts = rawCatPad.split(/\s+/).filter(Boolean);
                    if (parts.length === 1) return { top: parts[0], right: parts[0], bottom: parts[0], left: parts[0] };
                    if (parts.length === 2) return { top: parts[0], right: parts[1], bottom: parts[0], left: parts[1] };
                    if (parts.length === 3) return { top: parts[0], right: parts[1], bottom: parts[2], left: parts[1] };
                    if (parts.length >= 4) return { top: parts[0], right: parts[1], bottom: parts[2], left: parts[3] };
                    return null;
                })() : null;

                const resolvedCatPaddingTop = hasGranularCatPad
                    ? addUnit(catPadTop !== undefined && catPadTop !== '' ? catPadTop : (shorthandSides?.top || '0px'))
                    : undefined;
                const resolvedCatPaddingRight = hasGranularCatPad
                    ? addUnit(catPadRight !== undefined && catPadRight !== '' ? catPadRight : (shorthandSides?.right || '0px'))
                    : undefined;
                const resolvedCatPaddingBottom = hasGranularCatPad
                    ? addUnit(catPadBottom !== undefined && catPadBottom !== '' ? catPadBottom : (shorthandSides?.bottom || '0px'))
                    : undefined;
                const resolvedCatPaddingLeft = hasGranularCatPad
                    ? addUnit(catPadLeft !== undefined && catPadLeft !== '' ? catPadLeft : (shorthandSides?.left || '0px'))
                    : undefined;

                const btnNormalOpacity = parseAlpha(styleSettings.shopCatBtnOpacity !== undefined ? styleSettings.shopCatBtnOpacity : 1, 1);
                const btnActiveOpacity = parseAlpha(styleSettings.shopCatBtnActiveOpacity !== undefined ? styleSettings.shopCatBtnActiveOpacity : 1, 1);
                const rawNormalBg = styleSettings.shopCatBtnBg || '#f1f5f9';
                const rawActiveBg = styleSettings.shopCatBtnActiveBg || primaryColor || '#3b82f6';
                const resolvedNormalBg = (!rawNormalBg || rawNormalBg === 'transparent' || rawNormalBg === 'none' || btnNormalOpacity === 0)
                    ? 'transparent'
                    : hexToRgba(rawNormalBg, btnNormalOpacity);
                const resolvedActiveBg = (!rawActiveBg || rawActiveBg === 'transparent' || rawActiveBg === 'none' || btnActiveOpacity === 0)
                    ? 'transparent'
                    : hexToRgba(rawActiveBg, btnActiveOpacity);
                const getCatBtnOpacity = (isActive, isHidden = false) => {
                    return isHidden ? 0.65 : 1;
                };

                const resolvedFilterBackdropBlur = (() => {
                    if (styleSettings.shopFilterPanelBackdropBlur !== undefined) {
                        const b = styleSettings.shopFilterPanelBackdropBlur;
                        if (b === 'none' || b === '0' || b === '0px' || b === '') return undefined;
                        return b.startsWith('blur(') ? b : `blur(${b})`;
                    }
                    // Only default to blur(12px) if partially translucent (0 < opacity < 1), never on 0% clear / transparent
                    return (filterPanelOpacity < 1 && filterPanelOpacity > 0) ? 'blur(12px)' : undefined;
                })();

                return (
            <div
                className="shop-filter-bar-panel shop-filter-panel shop-filter-container"
                id="shop-filter-panel"
                data-testid="shop-filter-panel"
                style={{ 
                display: 'flex', 
                flexDirection: (searchPlacement === 'inside-top' || searchPlacement === 'inside-bottom') 
                    ? 'column' 
                    : ((styleSettings.shopFilterPanelDirection === 'row' && !isMobile) ? 'row' : 'column'), 
                gap: styleSettings.shopFilterPanelGap !== undefined ? addUnit(styleSettings.shopFilterPanelGap) : '20px', 
                background: resolvedFilterPanelBg,
                backdropFilter: resolvedFilterBackdropBlur,
                WebkitBackdropFilter: resolvedFilterBackdropBlur, 
                padding: styleSettings.shopFilterPanelPadding !== undefined ? addUnit(styleSettings.shopFilterPanelPadding) : '20px', 
                borderRadius: styleSettings.shopFilterPanelRadius !== undefined ? addUnit(styleSettings.shopFilterPanelRadius) : '12px', 
                boxShadow: (styleSettings.shopFilterPanelShadow === 'none' || styleSettings.shopCatBarShadow === 'none')
                    ? 'none'
                    : (styleSettings.shopFilterPanelShadow || styleSettings.shopCatBarShadow || '0 4px 6px -1px rgba(0,0,0,0.05)'), 
                border: styleSettings.shopFilterPanelBorder || '1px solid #e2e8f0', 
                marginBottom: styleSettings.shopFilterPanelMarginBottom !== undefined
                    ? addUnit(styleSettings.shopFilterPanelMarginBottom)
                    : (searchPlacement === 'below-panel' ? '16px' : (styleSettings.shopCategoriesPlacement === 'above-heading' ? '25px' : '40px')),
                alignItems: (styleSettings.shopFilterPanelDirection === 'row' && !isMobile && searchPlacement !== 'inside-top' && searchPlacement !== 'inside-bottom') ? 'center' : 'stretch',
                justifyContent: (styleSettings.shopFilterPanelDirection === 'row' && !isMobile) ? 'space-between' : 'stretch'
            }}>
                {/* Search & Sort Inside (Top) */}
                {searchPlacement === 'inside-top' && (
                    <div className="shop-search-sort-row" style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'center', width: '100%' }}>
                        {renderSearchInput({ flex: isMobile ? '1 1 100%' : '1 1 280px' })}
                        {renderSortSelect({ flex: isMobile ? '1 1 100%' : '0 0 auto' })}
                        {renderViewModeToggle({ flex: isMobile ? '1 1 100%' : '0 0 auto' })}
                    </div>
                )}

                {/* Search & Sort Inside (Left) */}
                {searchPlacement === 'inside-left' && (
                    <div className="shop-search-sort-row" style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'center' }}>
                        {renderSearchInput({ flex: isMobile ? '1 1 100%' : '0 1 240px' })}
                        {renderSortSelect({ flex: isMobile ? '1 1 100%' : '0 0 auto' })}
                        {renderViewModeToggle({ flex: isMobile ? '1 1 100%' : '0 0 auto' })}
                    </div>
                )}

                {/* Categories and Subcategories Container */}
                {!hideCategories && (
                <div className="shop-cat-container" style={{ 
                    display: 'flex', 
                    flexDirection: styleSettings.shopCatLayoutDirection || 'row',
                    flexWrap: 'wrap', 
                    gap: styleSettings.shopCatGap || '10px', 
                    alignItems: styleSettings.shopCatLayoutDirection === 'column' ? (styleSettings.shopCatAlign || 'flex-start') : 'center',
                    justifyContent: styleSettings.shopCatAlign || 'flex-start',
                    background: resolvedCatContainerBg,
                    padding: hasGranularCatPad ? undefined : (rawCatPad ? addUnit(rawCatPad) : undefined),
                    paddingTop: resolvedCatPaddingTop,
                    paddingRight: resolvedCatPaddingRight,
                    paddingBottom: resolvedCatPaddingBottom,
                    paddingLeft: resolvedCatPaddingLeft,
                    borderRadius: styleSettings.shopCatContainerRadius !== undefined && styleSettings.shopCatContainerRadius !== '' ? addUnit(styleSettings.shopCatContainerRadius) : undefined,
                    border: (styleSettings.shopCatContainerBorder === 'none') ? 'none' : (styleSettings.shopCatContainerBorder || undefined),
                    boxShadow: (styleSettings.shopCatContainerShadow === 'none') ? 'none' : (styleSettings.shopCatContainerShadow || undefined),
                    flex: (styleSettings.shopFilterPanelDirection === 'row' && !isMobile && (searchPlacement === 'inside-right' || searchPlacement === 'inside-left')) ? '1 1 auto' : undefined,
                    width: (styleSettings.shopCatLayoutDirection === 'column' || searchPlacement === 'inside-top' || searchPlacement === 'inside-bottom') ? '100%' : undefined
                }}>
                {!styleSettings.shopCatLabelHide && !settings.shopFilterLabelHide && (isEditing || (settings.shopFilterLabel !== undefined ? settings.shopFilterLabel.trim() !== '' : true)) && (
                    <Editable 
                        as="span" 
                        isEditing={isEditing} 
                        value={settings.shopFilterLabel !== undefined ? settings.shopFilterLabel : "Categories:"} 
                        onChange={e => update && update(['settings', 'shopFilterLabel'], e.target.value)} 
                        onStyleChange={(p, v) => update && update(['settings', `shopFilterLabel${p}`], v)}
                        placeholder="Categories:"
                        currentStyles={{ 
                            align: settings.shopFilterLabelAlign, 
                            size: styleSettings.shopCatLabelFontSize || settings.shopFilterLabelSize, 
                            color: styleSettings.shopCatLabelColor || settings.shopFilterLabelColor, 
                            fontFamily: styleSettings.shopCatLabelFontFamily || settings.shopFilterLabelFontFamily, 
                            preset: settings.shopFilterLabelPreset 
                        }}
                        style={{ 
                            ...resolveFontStyles('body', { 
                                align: settings.shopFilterLabelAlign, 
                                preset: settings.shopFilterLabelPreset 
                            }),
                            fontFamily: (styleSettings.shopCatLabelFontFamily && styleSettings.shopCatLabelFontFamily !== 'inherit') ? styleSettings.shopCatLabelFontFamily : undefined,
                            fontSize: addUnit(styleSettings.shopCatLabelFontSize || settings.shopFilterLabelSize || '0.9rem'),
                            color: styleSettings.shopCatLabelColor || settings.shopFilterLabelColor || '#334155',
                            fontWeight: styleSettings.shopCatLabelFontWeight || 'bold', 
                            textTransform: styleSettings.shopCatLabelTransform || 'none',
                            marginRight: styleSettings.shopCatLayoutDirection === 'row' ? '10px' : '0px'
                        }} 
                    />
                )}
                <div className="shop-cat-buttons-container" style={{
                    display: 'flex',
                    flexDirection: styleSettings.shopCatButtonsLayoutDirection || 'row',
                    flexWrap: 'wrap',
                    gap: styleSettings.shopCatButtonsGap || '8px',
                    justifyContent: styleSettings.shopCatAlign || 'flex-start',
                    alignItems: 'center',
                    width: styleSettings.shopCatButtonsLayoutDirection === 'column' ? '100%' : undefined
                }}>
                    {categories.map(cat => {
                        const normCat = cat ? cat.trim().toLowerCase() : '';
                        const isAllCat = cat === 'All' || cat === 'all' || normCat === 'all';
                        const catObj = dbCategories.find(c => 
                            (c.name && c.name.toLowerCase() === normCat) || 
                            (c.slug && c.slug.toLowerCase() === normCat) ||
                            slugify(c.name || '') === slugify(normCat)
                        );
                        const isCatHidden = isAllCat 
                            ? Boolean(settings.shopCatHideAll || styleSettings.shopCatHideAll)
                            : (hiddenCatNames.has(normCat) || hiddenCatNames.has(slugify(normCat)) || (catObj && (catObj.hidden === 1 || catObj.hidden === true || catObj.hidden === '1' || catObj.hidden === 'true')));
                        
                        if ((!isEditing || !showUnpublishedInEdit) && isCatHidden) return null;
                        const isCatActive = catMatchFn(cat, activeCategory);

                        if (isEditing) {
                            return (
                                <div 
                                    key={cat} 
                                    className="shop-cat-btn-edit-wrapper"
                                    style={{ 
                                        display: 'inline-flex', 
                                        alignItems: 'center', 
                                        justifyContent: 'center',
                                        background: isCatActive ? resolvedActiveBg : resolvedNormalBg,
                                        border: isCatHidden ? '1px dashed #f59e0b' : (isCatActive ? (styleSettings.shopCatBtnActiveBorder || styleSettings.shopCatBtnBorder || '1px solid #cbd5e1') : (styleSettings.shopCatBtnBorder || '1px solid #cbd5e1')),
                                        borderRadius: styleSettings.shopCatBtnRadius || '20px',
                                        padding: styleSettings.shopCatBtnPadding || '2px 4px 2px 8px',
                                        minWidth: styleSettings.shopCatBtnMinWidth ? addUnit(styleSettings.shopCatBtnMinWidth) : undefined,
                                        width: styleSettings.shopCatButtonsLayoutDirection === 'column' ? '100%' : (styleSettings.shopCatBtnWidth ? addUnit(styleSettings.shopCatBtnWidth) : undefined),
                                        gap: '4px',
                                        boxShadow: isCatActive ? (styleSettings.shopCatBtnActiveShadow || styleSettings.shopCatBtnShadow || 'none') : (styleSettings.shopCatBtnShadow || 'none'),
                                        opacity: getCatBtnOpacity(isCatActive, isCatHidden),
                                        transition: 'all 0.2s ease'
                                    }}
                                >
                                    <button
                                        type="button"
                                        onClick={(e) => handleCategoryClick(e, cat, catObj)}
                                        style={{
                                            background: 'transparent',
                                            border: 'none',
                                            color: isCatActive ? (styleSettings.shopCatBtnActiveColor || '#ffffff') : (styleSettings.shopCatBtnColor || '#1e293b'),
                                            cursor: 'pointer',
                                            fontFamily: (styleSettings.shopCatBtnFontFamily && styleSettings.shopCatBtnFontFamily !== 'inherit') ? styleSettings.shopCatBtnFontFamily : undefined,
                                            fontSize: addUnit(styleSettings.shopCatBtnFontSize || '0.85rem'),
                                            fontWeight: styleSettings.shopCatBtnFontWeight || '500',
                                            textTransform: styleSettings.shopCatBtnTransform || 'none',
                                            padding: '4px 6px',
                                            outline: 'none',
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            justifyContent: 'center'
                                        }}
                                        title={isCatHidden ? `${stripHtml(cat)} (Hidden from public users)` : stripHtml(cat)}
                                    >
                                        {renderCatContent(cat, catObj?.image)}
                                    </button>

                                    {/* Hide / Unhide Toggle button */}
                                    <button
                                        type="button"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            if (isAllCat) {
                                                if (update) {
                                                    update(['settings', 'shopCatHideAll'], !Boolean(settings.shopCatHideAll || styleSettings.shopCatHideAll));
                                                }
                                            } else if (handleToggleHideCategory) {
                                                handleToggleHideCategory(catObj || cat);
                                            }
                                        }}
                                        title={isCatHidden ? `"${cat}" is Hidden. Click to make Visible to public` : `"${cat}" is Visible. Click to Hide from public`}
                                        style={{
                                            background: isCatHidden ? '#fef3c7' : 'rgba(255, 255, 255, 0.88)',
                                            color: isCatHidden ? '#d97706' : '#64748b',
                                            border: '1px solid rgba(226, 232, 240, 0.9)',
                                            backdropFilter: 'blur(4px)',
                                            borderRadius: '50%',
                                            width: '24px',
                                            height: '24px',
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            cursor: 'pointer',
                                            padding: 0,
                                            fontSize: '0.75rem',
                                            boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
                                        }}
                                    >
                                        {isCatHidden ? <FaEyeSlash size={11} /> : <FaEye size={11} />}
                                    </button>

                                    {/* Move Left / Right Buttons */}
                                    {!isAllCat && handleMoveCategory && (
                                        <>
                                            <button
                                                type="button"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleMoveCategory(cat, 'left');
                                                }}
                                                title={`Move "${cat}" left`}
                                                style={{
                                                    background: 'rgba(255, 255, 255, 0.88)',
                                                    color: '#475569',
                                                    border: '1px solid rgba(226, 232, 240, 0.9)',
                                                    backdropFilter: 'blur(4px)',
                                                    borderRadius: '50%',
                                                    width: '24px',
                                                    height: '24px',
                                                    display: 'inline-flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    cursor: 'pointer',
                                                    padding: 0,
                                                    fontSize: '0.7rem',
                                                    boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
                                                }}
                                            >
                                                <FaArrowLeft size={10} />
                                            </button>
                                            <button
                                                type="button"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleMoveCategory(cat, 'right');
                                                }}
                                                title={`Move "${cat}" right`}
                                                style={{
                                                    background: 'rgba(255, 255, 255, 0.88)',
                                                    color: '#475569',
                                                    border: '1px solid rgba(226, 232, 240, 0.9)',
                                                    backdropFilter: 'blur(4px)',
                                                    borderRadius: '50%',
                                                    width: '24px',
                                                    height: '24px',
                                                    display: 'inline-flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    cursor: 'pointer',
                                                    padding: 0,
                                                    fontSize: '0.7rem',
                                                    boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
                                                }}
                                            >
                                                <FaArrowRight size={10} />
                                            </button>
                                        </>
                                    )}

                                    {/* Edit Category Details Modal button (for non-All categories) */}
                                    {!isAllCat && (
                                        <button
                                            type="button"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                setEditingCatModal({
                                                    isNew: false,
                                                    id: catObj?.id || `cat_${slugify(cat)}`,
                                                    name: catObj?.name || cat,
                                                    originalName: catObj?.name || cat,
                                                    slug: catObj?.slug || slugify(cat),
                                                    description: catObj?.description || '',
                                                    image: catObj?.image || '',
                                                    color: catObj?.color || '#3b82f6',
                                                    hidden: isCatHidden ? 1 : 0,
                                                    parentId: catObj?.parentId || null,
                                                    linkType: catObj?.linkType || 'category',
                                                    customUrl: catObj?.customUrl || '',
                                                    target: catObj?.target || '_self'
                                                });
                                            }}
                                            title="Edit category name, image, slug, description, or color"
                                            style={{
                                                background: 'rgba(255, 255, 255, 0.88)',
                                                color: '#475569',
                                                border: '1px solid rgba(226, 232, 240, 0.9)',
                                                backdropFilter: 'blur(4px)',
                                                borderRadius: '50%',
                                                width: '24px',
                                                height: '24px',
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                cursor: 'pointer',
                                                padding: 0,
                                                fontSize: '0.75rem',
                                                boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
                                            }}
                                        >
                                            <FaEdit size={11} />
                                        </button>
                                    )}
                                </div>
                            );
                        }

                            return (
                                <button
                                    key={cat}
                                    type="button"
                                    className="shop-cat-btn"
                                    onClick={(e) => handleCategoryClick(e, cat, catObj)}
                                    style={{
                                        background: isCatActive ? resolvedActiveBg : resolvedNormalBg,
                                        color: isCatActive ? (styleSettings.shopCatBtnActiveColor || '#ffffff') : (styleSettings.shopCatBtnColor || '#1e293b'),
                                        border: isCatHidden ? '1px dashed #f59e0b' : (isCatActive ? (styleSettings.shopCatBtnActiveBorder || styleSettings.shopCatBtnBorder) : styleSettings.shopCatBtnBorder),
                                        padding: styleSettings.shopCatBtnPadding,
                                        borderRadius: styleSettings.shopCatBtnRadius,
                                        minWidth: styleSettings.shopCatBtnMinWidth ? addUnit(styleSettings.shopCatBtnMinWidth) : undefined,
                                        cursor: 'pointer',
                                        fontFamily: (styleSettings.shopCatBtnFontFamily && styleSettings.shopCatBtnFontFamily !== 'inherit') ? styleSettings.shopCatBtnFontFamily : undefined,
                                        fontSize: addUnit(styleSettings.shopCatBtnFontSize || '0.85rem'),
                                        fontWeight: styleSettings.shopCatBtnFontWeight || '500',
                                        textTransform: styleSettings.shopCatBtnTransform || 'none',
                                        boxShadow: isCatActive ? (styleSettings.shopCatBtnActiveShadow || styleSettings.shopCatBtnShadow || 'none') : (styleSettings.shopCatBtnShadow || 'none'),
                                        transition: 'all 0.2s',
                                        opacity: getCatBtnOpacity(isCatActive, isEditing && isCatHidden),
                                        width: styleSettings.shopCatButtonsLayoutDirection === 'column' ? '100%' : (styleSettings.shopCatBtnWidth ? addUnit(styleSettings.shopCatBtnWidth) : undefined),
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        justifyContent: 'center'
                                    }}
                                    title={isCatHidden ? `${stripHtml(cat)} (Hidden from public users)` : stripHtml(cat)}
                                >
                                    {renderCatContent(cat, catObj?.image)}
                                </button>
                            );
                        })}

                        {isEditing && (
                            <>
                                <button
                                    type="button"
                                    onClick={() => setEditingCatModal({
                                        isNew: true,
                                        name: '',
                                        slug: '',
                                        description: '',
                                        image: '',
                                        color: '#3b82f6',
                                        hidden: 0,
                                        parentId: null,
                                        linkType: 'category',
                                        customUrl: '',
                                        target: '_self'
                                    })}
                                    style={{
                                        background: '#eff6ff',
                                        color: '#2563eb',
                                        border: '1px dashed #3b82f6',
                                        borderRadius: styleSettings.shopCatBtnRadius || '20px',
                                        padding: '5px 12px',
                                        cursor: 'pointer',
                                        fontSize: '0.8rem',
                                        fontWeight: 600,
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: '5px',
                                        transition: 'all 0.2s'
                                    }}
                                    title="Add a new category"
                                >
                                    <FaPlus size={10} /> Add Category
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setIsRearrangeModalOpen(true)}
                                    style={{
                                        background: '#eff6ff',
                                        color: '#2563eb',
                                        border: '1px dashed #3b82f6',
                                        borderRadius: styleSettings.shopCatBtnRadius || '20px',
                                        padding: '5px 12px',
                                        cursor: 'pointer',
                                        fontSize: '0.8rem',
                                        fontWeight: 600,
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: '5px',
                                        transition: 'all 0.2s'
                                    }}
                                    title="Rearrange category order"
                                >
                                    <FaSortAmountDown size={10} /> Rearrange Categories
                                </button>
                            </>
                        )}
                    </div>
                    {subCategoriesOfActive.length > 0 && (
                        <div style={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            gap: styleSettings.shopSubCatGap || '6px',
                            width: '100%',
                            marginTop: '10px',
                            paddingLeft: styleSettings.shopCatLayoutDirection === 'column' ? '0px' : '20px',
                            borderLeft: styleSettings.shopCatLayoutDirection === 'column' ? 'none' : (styleSettings.shopSubCatDivider || '2px solid #cbd5e1')
                        }}>
                            <span style={{ fontSize: '0.8rem', fontWeight: 'bold', color: '#64748b', display: 'flex', alignItems: 'center', marginRight: '6px' }}>
                                Sub-categories:
                            </span>
                            <button
                                type="button"
                                onClick={(e) => {
                                    if (e && e.preventDefault) e.preventDefault();
                                    if (setActiveSubCategory) setActiveSubCategory('All');
                                    const _relUrl = activeCategory === 'All' ? normalizeAppUrl('/shop') : normalizeAppUrl(`/products/${slugify(activeCategory)}`);
                                    if (navigate) {
                                        navigate(_relUrl, { preventScrollReset: true, state: { preventScrollReset: true } });
                                    } else {
                                        const _base = (getAppBase() || '').replace(/\/$/, '');
                                        const targetUrl = _base ? `${_base}${_relUrl}` : _relUrl;
                                        try {
                                            window.history.pushState({ preventScrollReset: true }, '', targetUrl);
                                        } catch (navErr) {}
                                    }
                                }}
                                style={{
                                    background: subCatMatchFn(activeSubCategory, 'All') ? resolvedActiveBg : resolvedNormalBg,
                                    color: subCatMatchFn(activeSubCategory, 'All') ? (styleSettings.shopCatBtnActiveColor || '#ffffff') : (styleSettings.shopCatBtnColor || '#1e293b'),
                                    border: 'none',
                                    padding: '4px 10px',
                                    borderRadius: styleSettings.shopCatBtnRadius || '12px',
                                    cursor: 'pointer',
                                    opacity: getCatBtnOpacity(subCatMatchFn(activeSubCategory, 'All'), false),
                                    transition: 'all 0.2s',
                                    fontFamily: (styleSettings.shopCatBtnFontFamily && styleSettings.shopCatBtnFontFamily !== 'inherit') ? styleSettings.shopCatBtnFontFamily : undefined,
                                    fontSize: styleSettings.shopCatBtnFontSize ? `calc(${styleSettings.shopCatBtnFontSize} * 0.95)` : '0.8rem',
                                    fontWeight: styleSettings.shopCatBtnFontWeight || '500',
                                    textTransform: styleSettings.shopCatBtnTransform || 'none'
                                }}
                            >
                                All
                            </button>
                            {subCategoriesOfActive.map(subCat => {
                                const isSubActive = subCatMatchFn(subCat.name, activeSubCategory);
                                const isSubHidden = subCat.hidden === 1 || subCat.hidden === true || subCat.hidden === '1' || subCat.hidden === 'true';

                                if ((!isEditing || !showUnpublishedInEdit) && isSubHidden) return null;
                                if (isEditing) {
                                    return (
                                        <div
                                            key={subCat.id || subCat.name}
                                            style={{
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                                background: isSubActive ? resolvedActiveBg : resolvedNormalBg,
                                                border: isSubHidden ? '1px dashed #f59e0b' : '1px solid #cbd5e1',
                                                borderRadius: styleSettings.shopCatBtnRadius || '12px',
                                                padding: '2px 4px 2px 8px',
                                                gap: '4px',
                                                opacity: getCatBtnOpacity(isSubActive, isSubHidden),
                                                transition: 'all 0.2s'
                                            }}
                                        >
                                            <button
                                                type="button"
                                                onClick={(e) => handleSubCategoryClick(e, subCat)}
                                                style={{
                                                    background: 'transparent',
                                                    border: 'none',
                                                    color: isSubActive ? (styleSettings.shopCatBtnActiveColor || '#ffffff') : (styleSettings.shopCatBtnColor || '#1e293b'),
                                                    cursor: 'pointer',
                                                    fontFamily: (styleSettings.shopCatBtnFontFamily && styleSettings.shopCatBtnFontFamily !== 'inherit') ? styleSettings.shopCatBtnFontFamily : undefined,
                                                    fontSize: styleSettings.shopCatBtnFontSize ? `calc(${styleSettings.shopCatBtnFontSize} * 0.95)` : '0.8rem',
                                                    fontWeight: styleSettings.shopCatBtnFontWeight || '500',
                                                    textTransform: styleSettings.shopCatBtnTransform || 'none',
                                                    padding: '2px 4px',
                                                    display: 'inline-flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center'
                                                }}
                                            >
                                                {renderCatContent(subCat.name, subCat.image, true)}
                                            </button>
                                            <button
                                                type="button"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    if (handleToggleHideCategory) handleToggleHideCategory(subCat);
                                                }}
                                                title={isSubHidden ? "Sub-category is Hidden. Click to make Visible" : "Sub-category is Visible. Click to Hide"}
                                                style={{
                                                    background: isSubHidden ? '#fef3c7' : '#ffffff',
                                                    color: isSubHidden ? '#d97706' : '#64748b',
                                                    border: '1px solid #e2e8f0',
                                                    borderRadius: '50%',
                                                    width: '20px',
                                                    height: '20px',
                                                    display: 'inline-flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    cursor: 'pointer',
                                                    padding: 0
                                                }}
                                            >
                                                {isSubHidden ? <FaEyeSlash size={10} /> : <FaEye size={10} />}
                                            </button>
                                            <button
                                                type="button"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setEditingCatModal({
                                                        isNew: false,
                                                        id: subCat.id,
                                                        name: subCat.name,
                                                        originalName: subCat.name,
                                                        slug: subCat.slug || slugify(subCat.name),
                                                        description: subCat.description || '',
                                                        image: subCat.image || '',
                                                        color: subCat.color || '#3b82f6',
                                                        hidden: isSubHidden ? 1 : 0,
                                                        parentId: subCat.parentId || null,
                                                        linkType: subCat.linkType || 'category',
                                                        customUrl: subCat.customUrl || '',
                                                        target: subCat.target || '_self'
                                                    });
                                                }}
                                                title="Edit sub-category details"
                                                style={{
                                                    background: '#ffffff',
                                                    color: '#475569',
                                                    border: '1px solid #e2e8f0',
                                                    borderRadius: '50%',
                                                    width: '20px',
                                                    height: '20px',
                                                    display: 'inline-flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    cursor: 'pointer',
                                                    padding: 0
                                                }}
                                            >
                                                <FaEdit size={10} />
                                            </button>
                                        </div>
                                    );
                                }

                                return (
                                    <button
                                        key={subCat.id}
                                        type="button"
                                        onClick={(e) => handleSubCategoryClick(e, subCat)}
                                        style={{
                                            background: isSubActive ? resolvedActiveBg : resolvedNormalBg,
                                            color: isSubActive ? (styleSettings.shopCatBtnActiveColor || '#ffffff') : (styleSettings.shopCatBtnColor || '#1e293b'),
                                            border: 'none',
                                            padding: '4px 10px',
                                            borderRadius: styleSettings.shopCatBtnRadius || '12px',
                                            cursor: 'pointer',
                                            opacity: getCatBtnOpacity(isSubActive, false),
                                            transition: 'all 0.2s',
                                            fontFamily: (styleSettings.shopCatBtnFontFamily && styleSettings.shopCatBtnFontFamily !== 'inherit') ? styleSettings.shopCatBtnFontFamily : undefined,
                                            fontSize: styleSettings.shopCatBtnFontSize ? `calc(${styleSettings.shopCatBtnFontSize} * 0.95)` : '0.8rem',
                                            fontWeight: styleSettings.shopCatBtnFontWeight || '500',
                                            textTransform: styleSettings.shopCatBtnTransform || 'none',
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            justifyContent: 'center'
                                        }}
                                    >
                                        {renderCatContent(subCat.name, subCat.image, true)}
                                    </button>
                                );
                            })}

                            {isEditing && (
                                <button
                                    type="button"
                                    onClick={() => {
                                        const activeParent = dbCategories.find(c => !c.parentId && (c.name === activeCategory || slugify(c.name) === slugify(activeCategory)));
                                        setEditingCatModal({
                                            isNew: true,
                                            name: '',
                                            slug: '',
                                            description: '',
                                            image: '',
                                            color: '#3b82f6',
                                            hidden: 0,
                                            parentId: activeParent?.id || null,
                                            linkType: 'category',
                                            customUrl: '',
                                            target: '_self'
                                        });
                                    }}
                                    style={{
                                        background: '#eff6ff',
                                        color: '#2563eb',
                                        border: '1px dashed #3b82f6',
                                        borderRadius: styleSettings.shopCatBtnRadius || '12px',
                                        padding: '3px 10px',
                                        cursor: 'pointer',
                                        fontSize: '0.75rem',
                                        fontWeight: 600,
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: '4px'
                                    }}
                                    title="Add subcategory"
                                >
                                    <FaPlus size={9} /> Add Sub-category
                                </button>
                            )}
                        </div>
                    )}
                </div>
                )}

                {/* Search & Sort Inside (Right - Default) */}
                {(searchPlacement === 'inside-right' || (!['inside-left', 'inside-top', 'inside-bottom', 'above-panel', 'below-panel', 'hidden'].includes(searchPlacement))) && (
                    <div className="shop-search-sort-row" style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: '10px', 
                        flexWrap: 'wrap', 
                        justifyContent: isMobile ? 'stretch' : 'flex-end', 
                        width: isMobile ? '100%' : undefined,
                        alignSelf: 'center'
                    }}>
                        {renderSearchInput({ flex: isMobile ? '1 1 100%' : '0 1 260px' })}
                        {renderSortSelect({ flex: isMobile ? '1 1 100%' : '0 0 auto' })}
                        {renderViewModeToggle({ flex: isMobile ? '1 1 100%' : '0 0 auto' })}
                    </div>
                )}

                {/* Search & Sort Inside (Bottom) */}
                {searchPlacement === 'inside-bottom' && (
                    <div className="shop-search-sort-row" style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'center', width: '100%' }}>
                        {renderSearchInput({ flex: isMobile ? '1 1 100%' : '1 1 280px' })}
                        {renderSortSelect({ flex: isMobile ? '1 1 100%' : '0 0 auto' })}
                        {renderViewModeToggle({ flex: isMobile ? '1 1 100%' : '0 0 auto' })}
                    </div>
                )}
            </div>
                );
            })()}
            {searchPlacement === 'below-panel' && (
                <div className="shop-search-sort-row" style={{ marginBottom: '40px', display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'center', justifyContent: styleSettings.shopSearchAlign === 'center' ? 'center' : (styleSettings.shopSearchAlign === 'flex-end' ? 'flex-end' : 'flex-start') }}>
                    {renderSearchInput({ flex: isMobile ? '1 1 100%' : '0 1 320px' })}
                    {renderSortSelect({ flex: isMobile ? '1 1 100%' : '0 0 auto' })}
                    {renderViewModeToggle({ flex: isMobile ? '1 1 100%' : '0 0 auto' })}
                </div>
            )}
            {/* Category Rearrange Modal */}
            <CategoryRearrangeModal
                isOpen={isRearrangeModalOpen}
                onClose={() => setIsRearrangeModalOpen(false)}
                categories={categories}
                handleReorderCategories={handleReorderCategories}
                handleMoveCategory={handleMoveCategory}
                handleSortCategoriesAlpha={handleSortCategoriesAlpha}
                primaryColor={primaryColor}
            />
        </>
    );
};

export default ShopFilterBar;
